import { compact } from 'lodash';
import type { PropsWithChildren, RefObject } from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { OptionItemProps } from '../../components/Options/Options';
import Options from '../../components/Options/Options';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  MessageRefsContextProvider,
  useMessageRefsContext,
} from '../../utils/customHooks/MessageRefsContext';
import {
  CreateMessageReplyType,
  GroupType,
  MessageSubType,
  ReceivedMessageType,
  ReplyPurpose,
  SentMessageType,
} from '../../utils/enums';
import {
  isChatsLoading,
  selectAreAllRepliesLoaded,
  selectAreFilesSending,
  selectAreRepliesLoading,
  selectCurrentChat,
  selectCurrentChatReplies,
  selectError,
  selectScrollToBottom,
  selectShowLoadingReplies,
  setLatestThresholdLoadTime,
  setScrollToBottom,
} from '../ChatsList/chatListSlice';
import {
  deleteAReply,
  editMessageAction,
  fetchCurrentChat,
  fetchCurrentChatAndScrollToReply,
  fetchCurrentChatReplies,
  fetchCurrentChatRepliesByThreshold,
  fetchReply,
  fetchMessageReactionTypes,
  fetchSingleChat,
  markReplyAsImportantAction,
  onReplyToMessageClick,
  replyToMessageAction,
} from '../ChatsList/chatListSlice/actionCreators';
import type { Chat as ChatType, Reply } from '../ChatsList/chatListSlice/types';
import { selectUserId, selectUserPermissions } from '../Login/LoginSlice';
import {
  SChat,
  SChatContainer,
  SFilesLoaderWrapper,
  SRepliesLoader,
  SScrollDownBtw,
  SStickyInstructionsBar,
} from './Chat.styles';
import { AlarmInstructionsBar } from '../../components/AlarmInstructionsBar/AlarmInstructionsBar';
import { palette } from '../../theme/colors';
import { selectTypes } from '../Alarm/AlarmSlice';
import { fetchAllActiveAlarms, getEmergencyTypesAction } from '../Alarm/AlarmSlice/actionCreators';
import { useIsSilentMode } from '../../utils/customHooks/useIsSilentMode';
import {
  checkHasReply,
  generateMessageReplyToMessage,
  generateReplyToMessageBodyText,
  generateReplyToMessageIcon,
  getMessageHeaderSubTitle,
  isReplyInSameDay,
  isTheSameReplySender,
  isTheSameReplySenderAsNext,
} from './helpers';
import { decode } from 'html-entities';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { saveDocumentToServer } from '../../apis/mediaAPI';
import BlockedAlarmIcon from '../../assets/imgs/chats/blocked-alarm-banner-icon.svg';
import Copy from '../../assets/imgs/chats/copy.svg';
import Delete from '../../assets/icons/trash-can--white.svg';
import Pencil from '../../assets/imgs/chats/edit-white.svg';
import Forward from '../../assets/imgs/chats/forward.svg';
import GreenCheck from '../../assets/imgs/chats/green-check.svg';
import ImportantMark from '../../assets/icons/important-mark.svg';
import ReadReceiptsIcon from '../../assets/imgs/chats/read-receipts.svg';
import ReplyIcon from '../../assets/imgs/chats/reply.svg';
import { ReactComponent as ScrollDownArrow } from '../../assets/imgs/chats/scroll-down-arrow.svg';
import CallAlertPhone from '../../assets/icons/warning-triangle.svg';
import ChatBox from '../../components/Chat/ChatBox';
import { ChatScreenTopBar } from '../../components/ChatScreenTopBar/ChatScreenTopBar';
import Loader from '../../components/Loader/Loader';
import { BlockedStateBanner } from '../../components/MessageItem/BlockedStateBanner/BlockedStateBanner';
import { BlockedStateBottomSheet } from '../../components/MessageItem/BlockedStateBottomSheet/BlockedStateBottomSheet';
import { AlarmBlockedStateSeparator } from '../../components/MessageItem/BlockedStateSeparator/AlarmBlockedStateSeparator';
import { BlockedStateSeparator } from '../../components/MessageItem/BlockedStateSeparator/BlockedStateSeparator';
import ChatReply from '../../components/MessageItem/ChatReply/ChatReply';
import { Disclaimer } from '../../components/MessageItem/Disclaimer/Disclaimer';
import { MessageItem } from '../../components/MessageItem/MessageItem';
import { Modal } from '../../components/Modal/Modal';
import { useConfirmation } from '../../utils/ConfirmationServiceContext/confirmationContext';
import { useLayoutContext } from '../../utils/customHooks/LayoutContext';
import { useMapModal } from '../../utils/customHooks/useMapModal';
import { isDesktop } from '../../utils/isDesktop';
import {
  setForwardedMessageID,
  setMessageContentProperty,
} from '../CreateMessage/createMessageSlice';
import { refreshUser, refreshUserPermissions } from '../Login/LoginSlice/actionCreators';
import type { MessageReplyModel } from './models';
import { MessageReactionsBottomSheet } from '../../components/MessageReactionsBottomSheet/MessageReactionsBottomSheet';
import MessageOptionsReactionsSelectionRow from '../../components/MessageOptionsReactionsSelectionRow/MessageOptionsReactionsSelectionRow';
import { useChatDraft } from '../../utils/customHooks/useChatDraft';

export type ReplyToMessageIcon = {
  src: string;
  classPresets?: string;
};
export type ReplyToMessageShorted = {
  senderName: string;
  messageText: string;
  messageId: number;
  messageIcon?: ReplyToMessageIcon;
  isReplyToInitialMessage?: boolean;
  isDeleted?: boolean;
};

type ChatProps = {
  id: string;
  setSubMessage?: boolean;
  fromHoldingStatment?: boolean;
  setSubMessageText?: React.Dispatch<string>;
};

export const Chat = (props: ChatProps) => {
  const {
    id,
    fromHoldingStatment: fromHoldingStatement = false,
    setSubMessage,
    setSubMessageText,
  } = props;
  const REPLIES_PER_PAGE = 15;
  const message = useAppSelector(selectCurrentChat);
  const replies = useAppSelector(selectCurrentChatReplies);
  const firstVisibleReplyIndex = replies.findIndex(isReplyRenderedAsBubble);
  const loading = useAppSelector(isChatsLoading);
  const userId = useAppSelector(selectUserId);
  const error = useAppSelector(selectError);
  const userPermissions = useAppSelector(selectUserPermissions);
  const areFilesSending = useAppSelector(selectAreFilesSending);
  const areRepliesLoading = useAppSelector(selectAreRepliesLoading);
  const showLoadingReplies = useAppSelector(selectShowLoadingReplies);
  const areAllRepliesLoaded = useAppSelector(selectAreAllRepliesLoaded);
  const needScrollToBottom = useAppSelector(selectScrollToBottom);
  const emergencyTypes = useAppSelector(selectTypes);
  const areAllRepliesLoadedRef = useRef(areAllRepliesLoaded); //[Web version] Is used to load chat info in interval only when  areAllRepliesLoaded = true

  const confirm = useConfirmation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { scrollToReplyId, scrollToMessage } = location.state || {};
  const { setMessage, setSubTitle, isMapOpen, setBelowHeaderContent } = useLayoutContext();

  const [activeAudio, setActiveAudio] = useState<number | null>(null);
  const [showBottomTabs, setShowBottomTabs] = useState(true);
  const [isBlockedBottomSheetOpen, setIsBlockedBottomSheetOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState<ChatType | Reply | null>(null);
  const [copyState, setCopyState] = useState<string>(Copy);
  const [editText, setEditText] = useState<string | undefined>(undefined);
  const [editTargetId, setEditTargetId] = useState<number | undefined>(undefined);
  const [replyMessageState, setReplyMessageState] = useState<ReplyToMessageShorted | undefined>(
    undefined
  );

  const { clearChatDraft, draftReplyId, draftEditId, draftText } = useChatDraft(
    id,
    replyMessageState?.messageId,
    editTargetId
  );

  const draftReplyRestoredRef = useRef<string | null>(null);
  const draftReplyFetchAttemptedRef = useRef<string | null>(null);
  const draftEditRestoredRef = useRef<string | null>(null);
  const draftEditFetchAttemptedRef = useRef<string | null>(null);

  useEffect(() => {
    setReplyMessageState(undefined);
    setEditText(undefined);
    setEditTargetId(undefined);
    draftReplyRestoredRef.current = null;
    draftReplyFetchAttemptedRef.current = null;
    draftEditRestoredRef.current = null;
    draftEditFetchAttemptedRef.current = null;
  }, [id]);

  useEffect(() => {
    if (!draftReplyId || draftReplyRestoredRef.current === id || message?.id !== Number(id)) return;

    let cancelled = false;
    const cleanup = () => {
      cancelled = true;
      draftReplyFetchAttemptedRef.current = null;
    };

    const selectedReply =
      draftReplyId === message.id ? message : replies.find(r => r.id === draftReplyId);
    if (!selectedReply) {
      if (draftReplyFetchAttemptedRef.current !== id) {
        draftReplyFetchAttemptedRef.current = id;
        (async () => {
          const fetchedReply = await dispatch(fetchReply(draftReplyId));
          if (cancelled || !fetchedReply) return;
          setReplyMessageState({
            messageIcon: generateReplyToMessageIcon(fetchedReply),
            messageText: generateReplyToMessageBodyText(fetchedReply),
            senderName: fetchedReply.senderName,
            messageId: fetchedReply.id,
          });
          draftReplyRestoredRef.current = id;
        })();
      }
      return cleanup;
    }

    setReplyMessageState({
      messageIcon: generateReplyToMessageIcon(selectedReply),
      messageText: generateReplyToMessageBodyText(selectedReply),
      senderName: selectedReply.senderName,
      messageId: selectedReply.id,
    });
    draftReplyRestoredRef.current = id;
    return cleanup;
  }, [id, draftReplyId, message, replies]);

  useEffect(() => {
    if (
      !draftEditId ||
      !draftText ||
      draftEditRestoredRef.current === id ||
      message?.id !== Number(id)
    )
      return;

    let cancelled = false;
    const cleanup = () => {
      cancelled = true;
      draftEditFetchAttemptedRef.current = null;
    };

    const editTarget =
      draftEditId === message.id ? message : replies.find(r => r.id === draftEditId);
    if (!editTarget) {
      if (draftEditFetchAttemptedRef.current !== id) {
        draftEditFetchAttemptedRef.current = id;
        (async () => {
          const fetchedReply = await dispatch(fetchReply(draftEditId));
          if (cancelled) return;
          if (!fetchedReply) {
            clearChatDraft();
            return;
          }
          setSelectedChat(fetchedReply);
          setEditTargetId(fetchedReply.id);
          setEditText(draftText);
          draftEditRestoredRef.current = id;
        })();
      }
      return cleanup;
    }

    setSelectedChat(editTarget);
    setEditTargetId(editTarget.id);
    setEditText(draftText);
    draftEditRestoredRef.current = id;
    return cleanup;
  }, [id, draftEditId, draftText, message, replies]);

  const { renderMapModal, toggleMapModal } = useMapModal(message);

  const scrollBtnRef = useRef<HTMLButtonElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const firstReplyRef = useRef<HTMLDivElement>(null);

  const { scrollToRef } = useMessageRefsContext();

  const isPersonalAlarm = message?.subType === MessageSubType.personalAlarm;
  const isHoldingStatementMessage = message?.type === SentMessageType.HoldingStatement;
  const isIamOkMessage = !!message?.musterID;
  const isOnCallAlertMessage = !!message?.onCallAlertID;

  const onCreated = async () => {
    if (scrollToReplyId || scrollToMessage) {
      const replyData: ReplyToMessageShorted = {
        messageId: scrollToReplyId,
        isReplyToInitialMessage: scrollToMessage ?? false,
        senderName: '',
        messageText: '',
      };
      dispatch(
        fetchCurrentChatAndScrollToReply(
          id,
          () => {
            dispatch(onReplyToMessageClick(replyData, scrollToRef, 'auto'));
          },
          REPLIES_PER_PAGE
        )
      );
    } else {
      dispatch(fetchCurrentChat(id, undefined, navigate, REPLIES_PER_PAGE, undefined, true));
    }
    dispatch(fetchMessageReactionTypes());
    dispatch(getEmergencyTypesAction());
    dispatch(fetchAllActiveAlarms());
    dispatch(setLatestThresholdLoadTime(new Date().toISOString().replace('Z', '')));
  };

  const handleMessageHeader = (messageObj: ChatType) => {
    let subText = '';
    const haveSubject = messageObj.subject && messageObj.subject.length;

    if (messageObj.emergencyTypeName !== null) {
      setMessage(messageObj.emergencyTypeName);
    } else {
      setMessage(t(`imOk_message`));
    }
    if (haveSubject) setMessage(messageObj?.subject?.toString());
    if (isPersonalAlarm) setMessage(t('alarm_personal_alarm'));
    if (isIamOkMessage) setMessage(t('home_muster'));
    if (isOnCallAlertMessage) setMessage(t('onCallAlert_screen'));
    if (isHoldingStatementMessage) {
      setMessage(haveSubject ? messageObj?.subject?.toString() : t('home_holdingStatement'));
    }
    if (messageObj?.type === 10) setMessage(t('logNote'));
    if (messageObj?.type === ReceivedMessageType.Broadcast && !messageObj?.subject) {
      setMessage(t('broadcast'));
    }
    const emergencyType = emergencyTypes.find(et => et.ID === messageObj.emergencyTypeId);
    const isSentToAll =
      messageObj.type === ReceivedMessageType.Alarm
        ? (emergencyType?.isSentToAllUsersInSubaccount ?? undefined)
        : undefined;
    if (isSentToAll) {
      const alarmGroupsCount = messageObj.groups?.length;
      const orgName = messageObj.Organization?.name;
      const allUsersWithGroupText = `${t('alarm_all_users')}${alarmGroupsCount ? `, ${alarmGroupsCount} ${alarmGroupsCount > 1 ? t('groups') : t('group')}` : ''}`;
      subText = orgName ? `${orgName} - ${allUsersWithGroupText}` : allUsersWithGroupText;
    } else {
      subText = getMessageHeaderSubTitle(messageObj, t);
    }
    if (setSubMessage) setSubTitle(subText);
  };

  useEffect(() => {
    onCreated();
  }, [id]);

  useEffect(() => {
    areAllRepliesLoadedRef.current = areAllRepliesLoaded;
  }, [areAllRepliesLoaded]);

  useEffect(() => {
    if (areFilesSending) {
      messagesEndRef.current?.scrollIntoView();
    }
  }, [areFilesSending]);

  useEffect(() => {
    if (message && location.state?.fromDetails) {
      handleMessageHeader(message);
      return;
    }
  }, [location.state]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined = undefined;

    const getRepliesByThreshold = () => {
      if (areAllRepliesLoadedRef.current) dispatch(fetchSingleChat(id.toString()));
      dispatch(
        fetchCurrentChatRepliesByThreshold({
          userId: userId ?? 0,
          lastMessageReference: messagesEndRef,
        })
      );
    };
    if (userId) {
      if (!isDesktop()) {
        interval = setInterval(getRepliesByThreshold, 10000);
      } else {
        getRepliesByThreshold();
      }
    }
    return () => {
      if (interval) clearInterval(interval);
      dispatch(refreshUser());
      dispatch(refreshUserPermissions());
      dispatch(setMessageContentProperty({ key: 'text', value: '' }));
      dispatch(setMessageContentProperty({ key: 'images', value: [] }));
      dispatch(setMessageContentProperty({ key: 'documents', value: [] }));
      dispatch(setMessageContentProperty({ key: 'location', value: null }));
    };
    // DONT CHANGE DEPENDENCIES! this will lead to infinite rerendering
    // since `user` updates itself on updateUser()
  }, [id]);

  useEffect(() => {
    if (error === 'Message is deleted') {
      confirm({
        title: 'message_not_found_title',
        description: 'message_deleted',
        onSubmit: () => navigate('/chat'),
        confirmText: 'ok',
        blockOutsideClick: true,
      });
    } else if (error === 'Request failed with status code 404') {
      confirm({
        title: 'message_not_found_title',
        description: 'message_not_found',
        onSubmit: () => navigate('/chat'),
        confirmText: 'ok',
        blockOutsideClick: true,
      });
    }
    // DONT CHANGE DEPENDENCIES! this will lead to infinite rerendering
    // since `confirm` updates itself on use
  }, [message, error]);

  useEffect(() => {
    if (needScrollToBottom) {
      messagesEndRef.current?.scrollIntoView();
    }
    dispatch(setScrollToBottom(false));
  }, [needScrollToBottom]);

  useLayoutEffect(() => {
    if (message && !isMapOpen) {
      handleMessageHeader(message);
      if (setSubMessageText && message?.subject) setSubMessageText(message.subject);
    }
  }, [message, location.key, isMapOpen, emergencyTypes]);

  // Options Handler - start

  const copyText = () => {
    if (selectedChat?.text) {
      navigator.clipboard.writeText(decode(selectedChat?.text));
      setCopyState(GreenCheck);

      setTimeout(() => {
        setSelectedChat(null);
        setOptionsOpen(false);
        setCopyState(Copy);
      }, 1000);
    }
  };

  const forward = () => {
    if (!selectedChat) return;
    if (message && selectedChat.id !== message.id) {
      navigate('forward', { state: { selectedReply: selectedChat } });
    } else {
      dispatch(setForwardedMessageID(selectedChat.id));
      navigate('forward');
    }
  };

  const deleteReply = () => {
    if (message && selectedChat) {
      dispatch(deleteAReply(message.id, selectedChat.id));
      setSelectedChat(null);
      setOptionsOpen(false);
    }
  };

  const markImportant = () => {
    if (!selectedChat || userId == null) return;
    dispatch(markReplyAsImportantAction(selectedChat.id, !isSelectedChatImportant, userId));
    setSelectedChat(null);
    setOptionsOpen(false);
  };

  const dismissReplyToMessage = () => {
    setReplyMessageState(undefined);
  };

  const replyToMessage = () => {
    if (message && selectedChat) {
      if (editTargetId !== undefined) clearChatDraft();
      setEditText(undefined);
      setEditTargetId(undefined);
      setReplyMessageState({
        messageIcon: generateReplyToMessageIcon(selectedChat),
        messageText: generateReplyToMessageBodyText(selectedChat),
        senderName: selectedChat.senderName,
        messageId: selectedChat.id,
      });
      setOptionsOpen(false);
    }
  };

  const editMessage = () => {
    if (message && selectedChat) {
      setReplyMessageState(undefined);
      setEditTargetId(selectedChat.id);
      setEditText(decode(selectedChat.text));
      setOptionsOpen(false);
    }
  };

  const handleSetEditText: React.Dispatch<React.SetStateAction<string | undefined>> = value => {
    const resolved = typeof value === 'function' ? value(editText) : value;
    if (resolved === undefined && editTargetId !== undefined) {
      setEditTargetId(undefined);
      clearChatDraft();
    }
    setEditText(value);
  };

  const handleReadReceipts = () => {
    if (message && selectedChat) {
      navigate(`read-receipts/${selectedChat.id}`, {
        state: { prevPath: location.pathname },
      });
    }
  };

  // Options Handler - end
  // is user admin in at least one group in the associated groups
  const isUserAdmin = compact(message?.groups?.map(group => group.admin)).length !== 0;
  const isMessageSender = message?.senderID === userId;

  const canReplyWhenBlocked = isUserAdmin || isMessageSender || userPermissions?.messages.mayBlock;

  const isRepliesEmpty = replies.length === 0;
  const isNoReplyTypeInitially =
    message?.replyType === CreateMessageReplyType.noReply && isRepliesEmpty;

  const isBlocked = message?.replyType === CreateMessageReplyType.noReply && !canReplyWhenBlocked;
  const canReply = !(
    isNoReplyTypeInitially ||
    isBlocked ||
    message?.type === ReceivedMessageType.Broadcast
  );

  const isBroadcastMessage = message?.type === ReceivedMessageType.Broadcast;
  const isLogNoteMessage = message?.type === ReceivedMessageType.LogFile;
  const canHoldingStatementInteract = isHoldingStatementMessage ? fromHoldingStatement : true;
  const isAlarmMessage = selectedChat?.type === ReceivedMessageType.Alarm;
  const isActiveAlarm = isAlarmMessage && !(selectedChat as ChatType)?.ended;
  const isSender = selectedChat?.senderID === userId;
  const isReply = (selectedChat as ChatType)?.replies === undefined;
  const isSelectedChatImportant = isReply && (selectedChat as Reply)?.markedImportantBy != null;
  const showChatBox = editText?.length || (canHoldingStatementInteract && canReply);

  // const hasImages = selectedChat?.photoFileName || selectedChat?.photoFileNames?.length > 0
  // no longer needed for now since message can be copied if it has text regardless of images
  const hasText = !!selectedChat?.text?.trim();

  const isUserAllowedToDeleteAlarms = true;
  // according to: https://www.notion.so/catsycatstudio/Functionality-in-messages-for-features-21c093f5335f4a15aea804e6156165e9
  // not specified what is "relevant permission"
  // todo: update with the relevant permission property
  const canDelete =
    isReply && isSender && !(isLogNoteMessage || isActiveAlarm || !isUserAllowedToDeleteAlarms);
  const canEdit =
    isSender && !(isBroadcastMessage || isHoldingStatementMessage || isAlarmMessage || !hasText);
  // check if the linked group is hidden , if so disable reply to the (reply or message)
  const canForward = true;
  const canCopy = hasText && !(isAlarmMessage && !isReply);
  const canReadReceipts = !(isBroadcastMessage || isLogNoteMessage);
  const isAssociatedGroupHidden =
    !!message?.groups?.length && message.groups[0].type === GroupType.Hidden;

  const canReplyToMessage = message
    ? !(
        isBroadcastMessage ||
        isHoldingStatementMessage ||
        isAssociatedGroupHidden ||
        [CreateMessageReplyType.senderOnly, CreateMessageReplyType.noReply].includes(
          message?.replyType
        )
      )
    : false;

  const alarmInstructions =
    message?.emergencyTypePrewrittenText ||
    message?.emergencyTypePrewrittenTexts?.find(pt => pt.Language === i18n.language)?.Text ||
    message?.emergencyTypePrewrittenTexts?.find(pt => pt.Language === 'en')?.Text;
  const isAlarmTemplate =
    message?.type === ReceivedMessageType.Alarm &&
    emergencyTypes.find(et => et.ID === message?.emergencyTypeId)?.alarmTemplateId != null;
  const isSimplifiedUi =
    emergencyTypes.find(et => et.ID === message?.emergencyTypeId)?.isSimplifiedUi ?? false;
  const hasPinnedInstructionsBar = isAlarmTemplate && !!alarmInstructions && !message?.recalled;
  const alarmColor =
    message?.emergencyTypeColor && message.emergencyTypeColor.length > 0
      ? message.emergencyTypeColor
      : palette.redAlert;

  const shouldDisplayNewSilentModeBanner = useIsSilentMode();
  const showBlockingAlarmBanner =
    message?.hasBlockingAlarm &&
    !message?.recalled &&
    !hasPinnedInstructionsBar &&
    !shouldDisplayNewSilentModeBanner;

  const showOnCallAlertTopBar = isOnCallAlertMessage && !message?.OnCallAlert?.deleted;
  const showIamOkTopBar = isIamOkMessage && !message?.musterDeleted;

  // the order of keys here means the order of options in the menu
  /* eslint-disable @typescript-eslint/no-shadow */
  // we don't care if OptionIds property is equal to existing const since we don't call them without OptionIds name
  enum OptionIds {
    ReplyToMessage,
    Forward,
    Edit,
    MarkImportant,
    ReadReceipts,
    Copy,
    Delete,
  }
  /* eslint-enable @typescript-eslint/no-shadow */

  const options: { [K in OptionIds]: OptionItemProps } = {
    // the order of objects here does not matter now, since keys are used in optionsToShow
    [OptionIds.ReplyToMessage]: {
      name: 'message_reply',
      icon: ReplyIcon,
      callback: replyToMessage,
    },
    [OptionIds.Forward]: {
      name: 'forward_message_text',
      icon: Forward,
      callback: forward,
    },
    [OptionIds.Edit]: {
      name: 'messages_edit',
      icon: Pencil,
      callback: editMessage,
    },
    [OptionIds.MarkImportant]: {
      name: isSelectedChatImportant ? 'message_unmark_important' : 'message_mark_important',
      icon: ImportantMark,
      callback: markImportant,
    },
    [OptionIds.ReadReceipts]: {
      name: 'message_read_receipts',
      icon: ReadReceiptsIcon,
      callback: handleReadReceipts,
    },
    [OptionIds.Copy]: {
      name: 'messages_copy',
      icon: copyState,
      callback: copyText,
    },
    [OptionIds.Delete]: {
      name: 'messages_delete',
      icon: Delete,
      callback: deleteReply,
    },
  };

  const optionsToShow = () => {
    const canMarkImportant = isReply;
    const returnedOptions: OptionItemProps[] = [];
    if (isSimplifiedUi) {
      if (canMarkImportant) returnedOptions.push(options[OptionIds.MarkImportant]);
      if (canReadReceipts) returnedOptions.push(options[OptionIds.ReadReceipts]);
      if (canDelete) returnedOptions.push(options[OptionIds.Delete]);
      return returnedOptions;
    }
    if (canMarkImportant) returnedOptions.push(options[OptionIds.MarkImportant]);
    if (canForward) returnedOptions.push(options[OptionIds.Forward]);
    if (canEdit) returnedOptions.push(options[OptionIds.Edit]);
    if (canCopy) returnedOptions.push(options[OptionIds.Copy]);
    if (canDelete) returnedOptions.push(options[OptionIds.Delete]);
    if (canReplyToMessage) returnedOptions.push(options[OptionIds.ReplyToMessage]);
    if (canReadReceipts) returnedOptions.push(options[OptionIds.ReadReceipts]);
    return returnedOptions;
  };

  const playAudio = (_id: number | null) => {
    setActiveAudio(_id);
  };

  const editChatMessage = (text: string, reply: boolean) => {
    if (message?.id) {
      if (!reply) {
        dispatch(editMessageAction(text, message?.id));
      } else {
        dispatch(editMessageAction(text, message?.id, selectedChat?.id));
      }
      setSelectedChat(null);
      setEditText(undefined);
      setEditTargetId(undefined);
      clearChatDraft();
    }
  };

  const replyToChatMessage = (
    textMessage?: string,
    photoFiles: string[] = [],
    documentFiles: string[] = [],
    audioFiles: string[] = [],
    locationId?: number
  ) => {
    if (message) {
      const messageModel: MessageReplyModel = {
        messageId: message.id,
        text: textMessage,
        photoFileNames: photoFiles,
        audioFileNames: audioFiles,
        documentFileNames: documentFiles,
        locationId: locationId,
      };

      if (replyMessageState) {
        if (replyMessageState?.messageId === message.id) {
          messageModel.replyToInitialMessage = true;
        } else {
          messageModel.replyId = replyMessageState?.messageId;
        }

        setSelectedChat(null);
        setReplyMessageState(undefined);
      }
      dispatch(replyToMessageAction(messageModel));
      clearChatDraft();
    }
  };

  const onPhotosSend = async (imagesList: string[], messageText: string) => {
    if (imagesList.length > 0) {
      const text = messageText && messageText.length > 0 ? messageText : undefined;
      replyToChatMessage(text, imagesList);
    }
  };

  const onDocumentsSend = async (documentsList: File[], messageText: string) => {
    const documentsFilesNamesArray = [];
    let document: File;
    for await (document of documentsList) {
      const formData = new FormData();
      formData.append('document', document);
      const result = await saveDocumentToServer(formData);
      if (result) {
        documentsFilesNamesArray.push(result);
      }
    }
    const text = messageText && messageText.length > 0 ? messageText : undefined;
    if (documentsFilesNamesArray.length > 0) {
      replyToChatMessage(text, [], documentsFilesNamesArray);
    }
  };

  const changeRepliesPage = async () => {
    if (
      firstReplyRef.current &&
      firstReplyRef.current.getBoundingClientRect().y > 0 &&
      !areRepliesLoading &&
      !areAllRepliesLoaded
    ) {
      dispatch(fetchCurrentChatReplies(replies.length, REPLIES_PER_PAGE, scrollToRef));
    }
  };

  const scrolled = () => {
    changeRepliesPage();
    if (!messagesEndRef.current || !scrollBtnRef.current) {
      return;
    }
    const diff =
      messagesEndRef.current.getBoundingClientRect().y -
      scrollBtnRef.current.getBoundingClientRect().y;
    if (diff > 250) {
      scrollBtnRef.current.style.visibility = 'visible';
      scrollBtnRef.current.style.opacity = '1';
    } else {
      scrollBtnRef.current.style.visibility = 'hidden';
      scrollBtnRef.current.style.opacity = '0';
    }
  };

  const onWheel = async () => {
    changeRepliesPage();
  };

  const handleScrollDown = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const onBannerLinkClick = () => {
    setIsBlockedBottomSheetOpen(true);
  };

  const renderScrollDownBtn = () => {
    return (
      <SScrollDownBtw ref={scrollBtnRef} onClick={handleScrollDown} role="scrollDownButton">
        <ScrollDownArrow role="arrowDown" />
      </SScrollDownBtw>
    );
  };

  const mainMessageRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setBelowHeaderContent(
      hasPinnedInstructionsBar && (
        <StickyInstructionsBar
          mainMessageRef={mainMessageRef}
          chatContainerRef={chatContainerRef}
          observerDepsArray={[message?.id, areAllRepliesLoaded]}
        >
          <AlarmInstructionsBar alarmColor={alarmColor} instructions={alarmInstructions} />
        </StickyInstructionsBar>
      )
    );
  }, [message?.id, areAllRepliesLoaded, hasPinnedInstructionsBar, alarmColor, alarmInstructions]);

  if (!message || message.id !== Number(id) || loading || !userId) {
    return (
      <SChat id="chat">
        <Loader />
      </SChat>
    );
  }

  return (
    <SChat id="chat">
      {renderScrollDownBtn()}

      {showIamOkTopBar && (
        <ChatScreenTopBar
          link={`/muster/${message.musterID}`}
          title={message?.ended ? t('imOk_ended') : t('imOk_updatedStatus')}
          isGrayColor={message?.ended || message?.goodStatus || false}
        />
      )}

      {showOnCallAlertTopBar && (
        <ChatScreenTopBar
          link={`/oncall/${message.onCallAlertID}`}
          title={message?.ended ? t('onCallAlert_ended') : t('imOk_updatedStatus')}
          isGrayColor={message?.ended || message?.goodStatus || false}
          endSrc={CallAlertPhone}
        />
      )}

      {showBlockingAlarmBanner && (
        <BlockedStateBanner
          titleTx="messages_alarm_blocked_popup"
          linkTx="messages_alarm_blocked_popup_learn"
          onLinkClick={onBannerLinkClick}
          iconSource={BlockedAlarmIcon}
        />
      )}

      {showBlockingAlarmBanner && (
        <BlockedStateBottomSheet
          isOpen={isBlockedBottomSheetOpen}
          setIsOpen={setIsBlockedBottomSheetOpen}
        />
      )}
      {message && (
        <MessageRefsContextProvider>
          <SChatContainer
            ref={chatContainerRef}
            $topPadding={showIamOkTopBar || showOnCallAlertTopBar}
            onScroll={scrolled}
            onWheel={onWheel}
            role="chatContainer"
          >
            {renderMapModal()}
            {areAllRepliesLoaded && (
              <div ref={mainMessageRef}>
                <MessageItem
                  key={message.id}
                  alert={message.type === 2}
                  message={message}
                  isSameSenderAsNext={isTheSameReplySenderAsNext(
                    message,
                    MAIN_MESSAGE_INDEX,
                    replies
                  )}
                  onPlayClick={playAudio}
                  isAudioActive={message.id === activeAudio}
                  toggleOptions={() => {
                    setSelectedChat(message);
                    setOptionsOpen(!optionsOpen);
                  }}
                  onMapClick={toggleMapModal}
                />
              </div>
            )}
            {message.hasBlockingAlarm &&
              message.type === ReceivedMessageType.Alarm &&
              !shouldDisplayNewSilentModeBanner && (
                <AlarmBlockedStateSeparator
                  key={`${message?.id}-alarm-blocked-separator`}
                  isActivated
                />
              )}

            {areRepliesLoading && (
              <SRepliesLoader>
                <Loader size={40} />
              </SRepliesLoader>
            )}
            <div ref={firstReplyRef}></div>
            {replies.map((reply: Reply, index) => {
              const isThreadBLockStateSeparator = isThreadBlockStateSeparatorReply(reply);
              const isThreadBlocked = reply.purpose === ReplyPurpose.blockedComment;

              const isAlarmBlockedSeparator =
                message.hasBlockingAlarm &&
                (reply.purpose === ReplyPurpose.crisis || reply.emergencyRecall);
              const isAlarmEndedReply = isAlarmEndedDisclaimerReply(reply);

              if (isThreadBLockStateSeparator)
                return (
                  // eslint-disable-next-line react/jsx-key
                  <div>
                    <BlockedStateSeparator
                      key={`${reply.id}${index}`}
                      isBlocked={isThreadBlocked}
                    />
                  </div>
                );

              if (isAlarmEndedReply) {
                return (
                  <div key={`${reply.id}-${index}`}>
                    <Disclaimer
                      subject={message.emergencyTypeName ?? ''}
                      by={reply.senderName}
                      byLink={`/message-details/${message.id}/memberSettings/${reply.senderID}`}
                      byLinkState={{ prevPath: location.pathname }}
                      isSameUser={reply.senderID === userId}
                    />
                    {isAlarmBlockedSeparator && !shouldDisplayNewSilentModeBanner && (
                      <AlarmBlockedStateSeparator key={`${reply.id}${index}`} isActivated={false} />
                    )}
                  </div>
                );
              }
              // Alarm chats render the main message as a separate hero block, so the first reply
              // rendered as a bubble below it is visually the first message in the thread. Other
              // chat types render the main message as a regular bubble, which the index-0 fallback
              // to the main message's sender already handles.
              const isVisuallyFirstMessage =
                message.type === ReceivedMessageType.Alarm && index === firstVisibleReplyIndex;
              const isSameSender =
                !isVisuallyFirstMessage && isTheSameReplySender(message, reply, index, replies);
              return (
                <div key={reply.id}>
                  <ChatReply
                    reply={reply}
                    userId={userId}
                    isSameDay={isReplyInSameDay(message, reply, index, replies)}
                    isSameSender={isSameSender}
                    isSameSenderAsNext={isTheSameReplySenderAsNext(message, index, replies)}
                    emergencyTypeName={message.emergencyTypeName}
                    setShowBottomTabs={setShowBottomTabs}
                    onPlayClick={playAudio}
                    isAudioActive={reply.id === activeAudio}
                    toggleOptions={() => {
                      setSelectedChat(reply);
                      setOptionsOpen(!optionsOpen);
                    }}
                    onMapClick={toggleMapModal}
                    hasReply={checkHasReply(reply)}
                    ReplyToMessage={generateMessageReplyToMessage(reply, message)}
                  />
                </div>
              );
            })}
            <br />
            <div ref={messagesEndRef} />
            {areFilesSending && (
              <SFilesLoaderWrapper>
                <Loader />
              </SFilesLoaderWrapper>
            )}
          </SChatContainer>
          {showChatBox && (
            <ChatBox
              setEditText={handleSetEditText}
              editText={editText}
              replyMessage={replyMessageState}
              dismissReplyToMessage={dismissReplyToMessage}
              tabBar={showBottomTabs}
              setTabBar={setShowBottomTabs}
              onTextSend={
                editText
                  ? text => {
                      editChatMessage(text, isReply);
                    }
                  : replyToChatMessage
              }
              onPhotosSend={onPhotosSend}
              onDocumentsSend={onDocumentsSend}
              onLocationSend={(locationId, text) =>
                replyToChatMessage(text, [], [], [], locationId)
              }
              hideGalleryOption={isSimplifiedUi}
              hideLocationOption={isSimplifiedUi}
              hideFileOption={isSimplifiedUi}
            />
          )}
          <Options
            renderHeader={() =>
              isSimplifiedUi || (
                <MessageOptionsReactionsSelectionRow
                  selectedMessageId={selectedChat?.id}
                  handleClose={() => setOptionsOpen(false)}
                />
              )
            }
            items={optionsToShow()}
            isOpen={optionsOpen}
            setIsOpen={setOptionsOpen}
          />
          <Modal isOpen={showLoadingReplies} setIsOpen={() => {}}>
            <Loader />
          </Modal>
          <MessageReactionsBottomSheet />
        </MessageRefsContextProvider>
      )}
    </SChat>
  );
};

const StickyInstructionsBar = ({
  mainMessageRef,
  chatContainerRef,
  observerDepsArray,
  children,
}: PropsWithChildren<{
  mainMessageRef: RefObject<HTMLDivElement>;
  chatContainerRef: RefObject<HTMLDivElement>;
  observerDepsArray: unknown[];
}>) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const target = mainMessageRef.current;
    const root = chatContainerRef.current;
    if (!target || !root) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(!entry.isIntersecting && entry.boundingClientRect.height > 0),
      {
        root,
        threshold: 0,
      }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, observerDepsArray);

  return <SStickyInstructionsBar $visible={isVisible}>{children}</SStickyInstructionsBar>;
};

// The main message renders right above the oldest reply, see isTheSameReplySenderAsNext.
const MAIN_MESSAGE_INDEX = -1;

const isThreadBlockStateSeparatorReply = (reply: Reply) =>
  reply.purpose === ReplyPurpose.blockedComment || reply.purpose === ReplyPurpose.unBlockedComment;

const isAlarmEndedDisclaimerReply = (reply: Reply) => !!reply.emergencyRecall;

const isReplyRenderedAsBubble = (reply: Reply) =>
  !isThreadBlockStateSeparatorReply(reply) && !isAlarmEndedDisclaimerReply(reply);
